/**
 * PolishBatchPanel
 * --------------------------------------------------------------------------
 * Safety-manager tool that:
 *   1. Sweeps the DB for rough-draft descriptions (Incidents/CAPAs/Audit Findings).
 *   2. Runs a preview polish (no DB write) and shows a side-by-side diff.
 *   3. Lets the manager approve/reject each item, then commits only the approved
 *      polishes to the DB.
 *   4. Every commit is logged to `polish_batch_runs` (audit history).
 *
 * Access: safety_manager / quality_manager / admin (backend-enforced).
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Sparkles, ChevronDown, ChevronUp, Loader2, CheckCircle2, RefreshCw,
  History, X, Check, ArrowLeft, Undo2, RotateCcw,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const KIND_LABELS = {
  incident: 'Incident',
  capa: 'CAPA',
  audit_finding: 'Audit Finding',
};

// Expandable row inside the Batch History drawer. Loads on-demand item detail
// and offers per-item "Undo" while the 24-hour audit window is still open.
function HistoryRunRow({ run, token, onUndone }) {
  const [expanded, setExpanded] = React.useState(false);
  const [undoing, setUndoing] = React.useState(null); // item_id currently being undone
  const within24h = !!run.within_24h;

  const undoItem = async (item) => {
    setUndoing(item.id);
    try {
      const r = await fetch(`${API_BASE}/api/voice-incident/polish-batch/undo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          run_id: run.id, item_id: item.id, kind: item.kind,
          parent_id: item.parent_id, field: item.field,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      toast.success('Restored original text.', {
        description: `${KIND_LABELS[item.kind] || item.kind} · ${item.id.slice(0, 8)}`,
      });
      onUndone?.();
    } catch (e) {
      toast.error(`Undo failed: ${e.message}`);
    } finally {
      setUndoing(null);
    }
  };

  return (
    <div className="text-[12px] border-b border-slate-800 pb-1.5 last:border-0" data-testid={`history-run-${run.id}`}>
      <button
        type="button"
        className="w-full flex items-center justify-between hover:bg-slate-900/60 rounded px-1.5 py-1 text-left"
        onClick={() => setExpanded((v) => !v)}
        data-testid={`history-run-toggle-${run.id}`}
      >
        <div className="flex flex-col">
          <span className="text-slate-200">
            {new Date(run.created_at).toLocaleString()} — <span className="text-slate-400">{run.user_name || run.user_id}</span>
          </span>
          <span className="text-[10px] text-slate-500">Run #{run.id.slice(0, 8)} · {(run.items || []).length} items{within24h ? '' : ' · undo window closed'}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge className="bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 text-[10px]">
            {run.updated} polished{run.failed_count ? ` · ${run.failed_count} failed` : ''}
          </Badge>
          {expanded ? <ChevronUp className="w-3 h-3 text-slate-500" /> : <ChevronDown className="w-3 h-3 text-slate-500" />}
        </div>
      </button>
      {expanded && (
        <div className="mt-2 space-y-1.5 pl-2 border-l border-slate-800">
          {(run.items || []).length === 0 && (
            <div className="text-[11px] text-slate-500 italic">No items in this run.</div>
          )}
          {(run.items || []).map((it) => {
            const canUndo = !!it.undo_available && !it.undone;
            const isUndoing = undoing === it.id;
            return (
              <div key={it.id} className="flex items-start gap-2 py-1" data-testid={`history-item-${it.id}`}>
                <Badge className="bg-slate-800 border-slate-700 text-slate-300 text-[10px] mt-0.5">
                  {KIND_LABELS[it.kind] || it.kind}{it.field && it.field !== 'description' ? ` · ${it.field}` : ''}
                </Badge>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-slate-300 truncate">{it.polished_snippet}</div>
                  <div className="text-[10px] text-slate-500 italic mt-0.5">
                    Was: <span className="text-slate-400">{(it.original_snippet || '').slice(0, 90)}{(it.original_snippet || '').length > 90 ? '…' : ''}</span>
                  </div>
                </div>
                {it.undone ? (
                  <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-[10px]">Reverted</Badge>
                ) : canUndo ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => undoItem(it)}
                    disabled={isUndoing}
                    className="h-6 px-2 text-[10px] text-amber-300 hover:bg-amber-500/10"
                    data-testid={`history-undo-${it.id}`}
                    title="Restore the original text (within 24h of the batch commit)"
                  >
                    {isUndoing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                    Undo
                  </Button>
                ) : (
                  <span className="text-[10px] text-slate-600 italic pt-1">24h passed</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// UI stages — kept as a state machine so the panel body renders the
// correct step (scan → preview diffs → done) without deep prop-drilling.
const STAGES = { SCAN: 'scan', PREVIEWING: 'previewing', PREVIEW: 'preview', COMMITTING: 'committing' };

export default function PolishBatchPanel({ token, userRole }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState(STAGES.SCAN);
  const [previews, setPreviews] = useState([]);        // {kind, id, parent_id, field, title, original, polished, status}
  const [approved, setApproved] = useState({});        // key(preview) -> bool
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);

  const allowed = ['admin', 'safety_manager', 'quality_manager'].includes((userRole || '').toLowerCase());

  const scan = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/voice-incident/polish-batch/candidates?kind=all&limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setItems(j.items || []);
      setCounts(j.counts || {});
      const sel = {};
      (j.items || []).forEach((it) => { sel[key(it)] = true; });
      setSelected(sel);
    } catch (e) {
      toast.error(`Could not scan: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [token, allowed]);

  useEffect(() => { if (open && stage === STAGES.SCAN) scan(); }, [open, stage, scan]);

  if (!allowed) return null;

  const key = (it) => `${it.kind}:${it.id}:${it.field || 'description'}`;
  const selectedItems = items.filter((it) => selected[key(it)]);

  const runPreview = async () => {
    if (selectedItems.length === 0) return;
    if (selectedItems.length > 25) { toast.error('Max 25 items per preview.'); return; }
    setStage(STAGES.PREVIEWING);
    try {
      const r = await fetch(`${API_BASE}/api/voice-incident/polish-batch/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          items: selectedItems.map((it) => ({
            kind: it.kind, id: it.id, parent_id: it.parent_id, field: it.field,
          })),
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const j = await r.json();
      setPreviews(j.previews || []);
      // Pre-approve all successful ones by default
      const app = {};
      (j.previews || []).forEach((p) => { if (p.status === 'ready') app[key(p)] = true; });
      setApproved(app);
      setStage(STAGES.PREVIEW);
    } catch (e) {
      toast.error(`Preview failed: ${e.message}`);
      setStage(STAGES.SCAN);
    }
  };

  const runCommit = async () => {
    const approvedItems = previews.filter((p) => p.status === 'ready' && approved[key(p)]);
    if (approvedItems.length === 0) { toast.warning('Approve at least one item.'); return; }
    setStage(STAGES.COMMITTING);
    try {
      const r = await fetch(`${API_BASE}/api/voice-incident/polish-batch/commit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          items: approvedItems.map((p) => ({
            kind: p.kind, id: p.id, parent_id: p.parent_id, field: p.field, polished: p.polished,
          })),
        }),
      });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.detail || `HTTP ${r.status}`); }
      const j = await r.json();
      const failedCount = (j.failed || []).length;
      toast.success(`Committed ${j.updated} polish${j.updated === 1 ? '' : 'es'}${failedCount ? ` (${failedCount} failed)` : ''}. Run #${(j.run_id || '').slice(0, 8)}`);
      setPreviews([]);
      setApproved({});
      setStage(STAGES.SCAN);
      await scan();
    } catch (e) {
      toast.error(`Commit failed: ${e.message}`);
      setStage(STAGES.PREVIEW);
    }
  };

  const loadHistory = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/voice-incident/polish-batch/history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setHistory(j.runs || []);
    } catch (e) {
      toast.error(`History fetch failed: ${e.message}`);
    }
  };

  const toggleHistory = async () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next) await loadHistory();
  };

  const toggleAll = (checked) => {
    const sel = {};
    if (checked) items.forEach((it) => { sel[key(it)] = true; });
    setSelected(sel);
  };

  const readyCount = previews.filter((p) => p.status === 'ready').length;
  const approvedCount = previews.filter((p) => p.status === 'ready' && approved[key(p)]).length;

  return (
    <Card className="bg-slate-900/80 border-violet-500/30" data-testid="polish-batch-panel">
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-violet-500/5 transition-colors"
          data-testid="polish-batch-toggle"
        >
          <Sparkles className="w-5 h-5 text-violet-300" />
          <div className="flex-1 text-left">
            <div className="text-sm font-semibold text-white">Polish rough-draft descriptions</div>
            <div className="text-[11px] text-slate-400">
              Sweep incidents, CAPAs and audit findings; preview &amp; approve each rewrite before it&apos;s saved.
            </div>
          </div>
          {items.length > 0 && stage === STAGES.SCAN && (
            <Badge className="bg-violet-500/20 text-violet-200 border border-violet-500/40">
              {items.length} draft{items.length === 1 ? '' : 's'}
            </Badge>
          )}
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>

        {open && (
          <div className="border-t border-slate-800 p-4 space-y-3" data-testid="polish-batch-body">
            {/* -------- STAGE: SCAN -------- */}
            {stage === STAGES.SCAN && (
              <>
                <div className="flex items-center gap-3 flex-wrap">
                  <Button variant="outline" size="sm" onClick={scan} disabled={loading} className="border-slate-700 text-slate-300" data-testid="polish-batch-refresh">
                    {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    Rescan
                  </Button>
                  <div className="flex-1 flex items-center gap-2 text-[11px] text-slate-500">
                    <span>Incidents: <b className="text-slate-300">{counts.incident || 0}</b></span>
                    <span>• CAPAs: <b className="text-slate-300">{counts.capa || 0}</b></span>
                    <span>• Findings: <b className="text-slate-300">{counts.audit_finding || 0}</b></span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={toggleHistory} className="text-slate-400 hover:text-slate-200" data-testid="polish-batch-history-btn">
                    <History className="w-3 h-3 mr-1" /> History
                  </Button>
                  <Button onClick={runPreview} disabled={selectedItems.length === 0} className="bg-violet-600 hover:bg-violet-700" size="sm" data-testid="polish-batch-preview">
                    <Sparkles className="w-3 h-3 mr-1.5" /> Preview polish ({selectedItems.length})
                  </Button>
                </div>

                {historyOpen && (
                  <div className="rounded-md border border-slate-800 bg-slate-950/60 p-3 space-y-2 max-h-[420px] overflow-y-auto" data-testid="polish-batch-history">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between">
                      <span>Batch history (last 20 runs)</span>
                      <span className="text-slate-600 normal-case font-normal">Click a run to undo individual items (24h window)</span>
                    </div>
                    {history.length === 0 && (
                      <div className="text-[12px] text-slate-500 italic">No batches run yet.</div>
                    )}
                    {history.map((run) => (
                      <HistoryRunRow
                        key={run.id}
                        run={run}
                        token={token}
                        onUndone={loadHistory}
                      />
                    ))}
                  </div>
                )}

                {items.length === 0 && !loading && (
                  <div className="flex items-center gap-2 text-sm text-emerald-300 py-4">
                    <CheckCircle2 className="w-4 h-4" /> All descriptions look good — nothing to polish.
                  </div>
                )}
                {items.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 text-[11px] text-slate-400 border-b border-slate-800 pb-2">
                      <Checkbox
                        checked={items.every((it) => selected[key(it)])}
                        onCheckedChange={toggleAll}
                        data-testid="polish-batch-select-all"
                      />
                      Select all
                    </div>
                    <div className="max-h-96 overflow-y-auto space-y-1 pr-1">
                      {items.map((it) => (
                        <label key={key(it)} className="flex items-start gap-2 p-2 rounded hover:bg-slate-800/60 cursor-pointer transition-colors">
                          <Checkbox
                            checked={!!selected[key(it)]}
                            onCheckedChange={(v) => setSelected((prev) => ({ ...prev, [key(it)]: !!v }))}
                            data-testid={`polish-batch-item-${it.kind}-${it.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className="bg-slate-800 border-slate-700 text-slate-300 text-[10px]">
                                {KIND_LABELS[it.kind] || it.kind}{it.field && it.field !== 'description' ? ` · ${it.field.replace(/_/g, ' ')}` : ''}
                              </Badge>
                              <span className="text-xs text-slate-200 truncate">{it.title}</span>
                              <span className="text-[10px] text-slate-500">· {it.chars} chars</span>
                            </div>
                            <div className="text-[11px] text-slate-500 line-clamp-2 italic mt-0.5">{it.snippet}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {/* -------- STAGE: PREVIEWING (loading) -------- */}
            {stage === STAGES.PREVIEWING && (
              <div className="flex items-center gap-3 text-violet-200 py-6">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Polishing {selectedItems.length} drafts with GPT-5.2…</span>
              </div>
            )}

            {/* -------- STAGE: PREVIEW (approve/reject) -------- */}
            {stage === STAGES.PREVIEW && (
              <>
                <div className="flex items-center gap-3 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => { setStage(STAGES.SCAN); setPreviews([]); }} className="border-slate-700 text-slate-300" data-testid="polish-batch-back">
                    <ArrowLeft className="w-3 h-3 mr-1" /> Back
                  </Button>
                  <div className="flex-1 flex items-center gap-2 text-[11px] text-slate-400">
                    <Badge className="bg-emerald-500/20 text-emerald-200 border border-emerald-500/40">
                      {approvedCount} approved
                    </Badge>
                    <span className="text-slate-500">of {readyCount} ready</span>
                    {previews.length - readyCount > 0 && (
                      <Badge className="bg-rose-500/15 text-rose-300 border border-rose-500/40">
                        {previews.length - readyCount} error{previews.length - readyCount === 1 ? '' : 's'}
                      </Badge>
                    )}
                  </div>
                  <Button onClick={runCommit} disabled={approvedCount === 0} className="bg-emerald-600 hover:bg-emerald-700" size="sm" data-testid="polish-batch-commit">
                    <Check className="w-3 h-3 mr-1.5" /> Commit approved ({approvedCount})
                  </Button>
                </div>
                <div className="max-h-[520px] overflow-y-auto space-y-3 pr-1">
                  {previews.map((p) => {
                    const k = key(p);
                    const isApproved = !!approved[k];
                    const isError = p.status === 'error';
                    return (
                      <div key={k} className={`rounded-md border p-3 space-y-2 ${
                        isError
                          ? 'border-rose-500/40 bg-rose-500/5'
                          : isApproved
                            ? 'border-emerald-500/40 bg-emerald-500/5'
                            : 'border-slate-700 bg-slate-950/60'
                      }`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className="bg-slate-800 border-slate-700 text-slate-300 text-[10px]">
                            {KIND_LABELS[p.kind] || p.kind}{p.field && p.field !== 'description' ? ` · ${p.field.replace(/_/g, ' ')}` : ''}
                          </Badge>
                          <span className="text-xs text-slate-200 truncate flex-1">{p.title || '(untitled)'}</span>
                          {!isError && (
                            <>
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => setApproved((prev) => ({ ...prev, [k]: true }))}
                                className={`h-7 px-2 text-[11px] ${isApproved ? 'text-emerald-300' : 'text-slate-400 hover:text-emerald-300'}`}
                                data-testid={`polish-batch-approve-${p.kind}-${p.id}`}
                              >
                                <Check className="w-3 h-3 mr-1" /> Approve
                              </Button>
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => setApproved((prev) => ({ ...prev, [k]: false }))}
                                className={`h-7 px-2 text-[11px] ${!isApproved ? 'text-rose-300' : 'text-slate-400 hover:text-rose-300'}`}
                                data-testid={`polish-batch-reject-${p.kind}-${p.id}`}
                              >
                                <X className="w-3 h-3 mr-1" /> Reject
                              </Button>
                            </>
                          )}
                        </div>
                        {isError ? (
                          <div className="text-[11px] text-rose-300 italic">Error: {p.error || 'unknown'}</div>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Original</div>
                              <div className="text-[11px] text-slate-300 whitespace-pre-wrap bg-slate-950 border border-slate-800 rounded p-2 max-h-52 overflow-y-auto">
                                {p.original}
                              </div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1">Polished</div>
                              <div className="text-[11px] text-emerald-100 whitespace-pre-wrap bg-emerald-950/20 border border-emerald-500/30 rounded p-2 max-h-52 overflow-y-auto">
                                {p.polished}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {stage === STAGES.COMMITTING && (
              <div className="flex items-center gap-3 text-emerald-200 py-6">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Writing approved polishes back to the database…</span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
